import type React from "react";
import { styled } from "@mui/material";
import { memo, useCallback } from "react";

export const FOCUS_TARGET_WIDTH = 24;

const FocusTarget = styled("rect")(({ theme }) => ({
  fill: "transparent",
  outline: "none",
  "&:focus-visible": {
    stroke: theme.palette.primary.main,
    strokeWidth: 2,
  },
}));

export interface FocusablePointProps {
  index: number;
  x: number;
  y: number;
  height: number;
  label: string;
  isActive: boolean;
  describedBy?: string;
  registerTarget: (index: number, el: SVGRectElement | null) => void;
  onPointFocus: (index: number) => void;
  onPointBlur: (e: React.FocusEvent<SVGRectElement>) => void;
  onPointKeyDown: (e: React.KeyboardEvent<SVGRectElement>, index: number) => void;
}

function FocusablePoint(props: FocusablePointProps) {
  const {
    index,
    x,
    y,
    height,
    label,
    isActive,
    describedBy,
    registerTarget,
    onPointFocus,
    onPointBlur,
    onPointKeyDown,
  } = props;

  const ref = useCallback((el: SVGRectElement | null) => registerTarget(index, el), [index, registerTarget]);
  const handleFocus = useCallback(() => onPointFocus(index), [index, onPointFocus]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<SVGRectElement>) => onPointKeyDown(e, index),
    [index, onPointKeyDown]
  );

  return (
    <FocusTarget
      ref={ref}
      role="img"
      tabIndex={isActive ? 0 : -1}
      aria-label={label}
      aria-describedby={describedBy}
      x={x - FOCUS_TARGET_WIDTH / 2}
      y={y}
      width={FOCUS_TARGET_WIDTH}
      height={height}
      rx={4}
      pointerEvents="none"
      onFocus={handleFocus}
      onBlur={onPointBlur}
      onKeyDown={handleKeyDown}
    />
  );
}

export default memo(FocusablePoint);
